import {EditorView} from "prosemirror-view";
import {findChildren} from "prosemirror-utils";

import {FileNode, ImageNode} from "../module/schema";
import {dataUrlToFile, urlToFile} from "../../utils/file/urlToFile";
import {uploadFileToS3} from "../../utils/file/ImageUploadLogic";
import {logErrorToSentry} from "../../utils/errors/logErrorToSentry";
import {compressImage} from "../../utils/file/compressImage";
import { IStorageService } from "../../platform/services/storage/StorageService";


export class EditorFileUploadManager {
    isDead: boolean = false;
    view: EditorView;
    processingSet: Set<string> = new Set();
    storageService: IStorageService;
    tenantId: string;

    constructor(view: EditorView, storageService: IStorageService, tenantId: string) {
        this.view = view;
        this.storageService = storageService;
        this.tenantId = tenantId;
        this.isDead = false;
    }

    uploadFile = async (node: FileNode | ImageNode, file?: File) => {
        const {src} = node.attrs;
        if (!src) return;

        if (this.processingSet.has(src)) return;
        this.processingSet.add(src);

        try {
            let fileToUpload: File | null = file || null;
            if (!fileToUpload) {
                if (src.startsWith('data:')) {
                    fileToUpload = dataUrlToFile(src);
                } else {
                    fileToUpload = await urlToFile(src);
                }
            }

            if (this.isDead || !fileToUpload) {
                this.processingSet.delete(src);
                return;
            }

            let compressedFile: File = fileToUpload;
            if (fileToUpload.type === 'image/jpeg' || fileToUpload.type === 'image/jpg') {
                const compressedBlob = await compressImage(fileToUpload, 80);
                if (compressedBlob) {
                    compressedFile = new File([compressedBlob], fileToUpload.name, {type: compressedBlob.type});
                }
            }

            const {s3FileName} = await uploadFileToS3(compressedFile, this.storageService, this.tenantId);
            if (this.isDead || !s3FileName) {
                this.processingSet.delete(src);
                return;
            }

            this.updateViewWithOurSrc(src, s3FileName);
        } catch (e) {
            logErrorToSentry(e as Error);
        } finally {
            this.processingSet.delete(src);
        }
    }

    updateViewWithOurSrc = (src: string, s3FileName: string) => {
        if (!this.view) return;
        const imageNodes = findChildren(
            this.view.state.doc,
            (node) => (node.type.name === 'image' || node.type.name === 'file') && node.attrs.src === src
        );

        let tr = this.view.state.tr;
        for (const {pos, node} of imageNodes) {
            const newAttrs = {
                ...node.attrs,
                src: null,
                s3FileName: s3FileName,
            };
            tr = tr.setNodeMarkup(pos, undefined, newAttrs);
        }
        this.processingSet.delete(src);
        if (src.startsWith("blob:")) {
            URL.revokeObjectURL(src);
        }
        this.view.dispatch(tr);
    }

    destroy() {
        this.isDead = true;
    }
}

let fileUploadManager: EditorFileUploadManager | null = null;
export const getEditorFileUploadManager = () => {
    /* all places that call this exists within editor and initFIleUploadManager is called during editor init */
    return fileUploadManager!;
}

export const initEditorFileUploadManager = (view: EditorView, storageService: IStorageService, tenantId: string) => {
    if (fileUploadManager) {
        fileUploadManager.destroy();
    }
    fileUploadManager = new EditorFileUploadManager(view, storageService, tenantId);
}
